// =============================================
// emailStore — client for emails into DMS (/api/dms/email)
// =============================================
// Two ways an email becomes documents:
//   • a dropped .eml / .msg file → parseEmailFile() asks the server for the documents it
//     carries; the upload queue then stores each like any other upload;
//   • the forward-to-file address → the server files the mail itself; this module only
//     reads/changes the address and lists what arrived.
// =============================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authToken(): string | null {
  return localStorage.getItem('akt_auth_token');
}

export const EMAIL_EXTENSIONS = ['.eml', '.msg'];

export interface EmailPart {
  kind: 'body' | 'attachment' | 'email';   // 'email' = message + attachments in one PDF
  filename: string;
  type: 'pdf' | 'png' | 'jpeg';
  base64: string;               // the raw file bytes (not gzip)
}

export interface EmailSkipped { filename: string; reason: string; }

export interface ParsedEmail {
  subject: string;
  from: string;
  date: string | null;
  note: string;                 // one line saying where the documents came from
  parts: EmailPart[];
  skipped: EmailSkipped[];
}

/**
 * Ask the server what documents an email file carries. `merge` = one document for the
 * whole email (the message page(s), then its attachments) instead of one each. Throws with a message meant for
 * the user (not signed in, too large, not an email, server unreachable).
 */
export async function parseEmailFile(file: File, merge: boolean): Promise<ParsedEmail> {
  const token = authToken();
  if (!token) throw new Error('Emails are read on the server — sign in and go online to upload one.');
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/dms/email/parse?filename=${encodeURIComponent(file.name)}${merge ? '&merge=1' : ''}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream', Authorization: `Bearer ${token}` },
      body: file,
    });
  } catch {
    throw new Error('Emails are read on the server, which could not be reached. Try again when you are online.');
  }
  if (!res.ok) {
    let message = `The email could not be read (${res.status}).`;
    try { message = (await res.json())?.message || message; } catch { /* keep the default */ }
    throw new Error(message);
  }
  return res.json();
}

const MIME_OF: Record<EmailPart['type'], string> = { pdf: 'application/pdf', png: 'image/png', jpeg: 'image/jpeg' };

/** One returned part as a File, ready for the normal upload pipeline. */
export function emailPartToFile(part: EmailPart): File {
  const binary = atob(part.base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], part.filename, { type: MIME_OF[part.type] });
}

// ---- Forward-to-file address ---------------------------------------------------------

export type EmailBodyMode = 'auto' | 'always' | 'never';

export interface InboxAddress {
  email: string | null;         // null while the server has no inbound domain configured
  token: string;
  client_id: number | null;     // set = the address of a client (Client's Accounts)
  body_mode: EmailBodyMode;
  allow_any_sender: boolean;
  allowed_senders: string;
  owner_type: string | null;
  owner_id: number | null;
  is_active: boolean;
}

export interface InboxEmail {
  inbound_email_id: number;
  from_email: string | null;
  from_name: string | null;
  subject: string | null;
  status: 'filed' | 'empty' | 'rejected' | 'duplicate' | 'error';
  reason: string | null;
  doc_count: number;
  document_ids: number[];
  skipped: EmailSkipped[];
  received_at: string;
}

export interface InboxState {
  enabled: boolean;             // is receiving switched on for this server?
  domain: string;
  address: InboxAddress | null;
  recent?: InboxEmail[];
}

async function inboxRequest<T = InboxState>(method: string, path: string, body?: unknown): Promise<T> {
  const token = authToken();
  if (!token) throw new Error('Sign in to use the email address.');
  const res = await fetch(`${API_BASE}/dms/email/inbox${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status}).`;
    try { message = (await res.json())?.message || message; } catch { /* keep the default */ }
    throw new Error(message);
  }
  return res.json();
}

// `clientId` = a client's address (Client's Accounts) instead of the login's own.
// Addresses are made automatically: getInbox() returns one even the first time.
const clientQs = (clientId?: number | null) => (clientId != null ? `?client_id=${encodeURIComponent(String(clientId))}` : '');

export const getInbox = (clientId?: number | null) => inboxRequest('GET', clientQs(clientId));
export const createInboxAddress = (regenerate = false, clientId?: number | null) =>
  inboxRequest('POST', `/address${clientQs(clientId)}`, { regenerate });
export const saveInboxSettings = (
  settings: Partial<Omit<InboxAddress, 'email' | 'token' | 'client_id'>>, clientId?: number | null,
) => inboxRequest('PUT', `/address${clientQs(clientId)}`, settings);

export interface ClientInbox { client_id: number; name: string; address: InboxAddress | null; }

/** Every client of the account with its forwarding address. */
export const listClientInboxes = () =>
  inboxRequest<{ enabled: boolean; domain: string; clients: ClientInbox[] }>('GET', '/clients');
